'use client';
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Folder, FolderPlus, FolderUp } from 'lucide-react';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { ModalReport, type ModalReportItem } from './ModalReport';
import { OperationFailureReport } from './OperationFailureReport';
import { ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { useCreateDirectory } from '../../lib/mutations';
import { interpolate, useTranslation } from '../../lib/i18n';
import { holdsAnswer, releasing } from '../../lib/saveFeedback';
import { EmptyState, ErrorState, LoadingState } from './states';

interface DirectoryPickerProps {
  initialPath?: string;
  onSelect: (path: string) => void;
  onClose: () => void;
  /** Project registration can opt into directory creation. */
  allowCreateDirectory?: boolean;
};

// The failure and the success of a create are ONE card slot: a retry's outcome replaces the earlier card in place.
const CREATE_CARD_KEY = 'directory-create';

/** Server-side directory browser. Drills into sub-directories on click, climbs to the parent, and confirms
 * the currently-open folder. Admin-only on the daemon; directory names only, never file contents. */
export function DirectoryPicker({ initialPath, onSelect, onClose, allowCreateDirectory = false }: DirectoryPickerProps) {
  const { t } = useTranslation();
  const folderNameId = useId();
  const [path, setPath] = useState<string | undefined>(initialPath?.trim() || undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  // The one refusal that names the value: a folder of this name already exists. It stays with the field.
  const [duplicate, setDuplicate] = useState(false);
  // Raised from inside the Modal (`ModalReport`), so the card belongs to this picker: it shows in the picker's own
  // toast band and ends when the picker closes, instead of binding to the page behind.
  const [report, setReport] = useState<ModalReportItem | null>(null);
  const queryClient = useQueryClient();
  // A create whose outcome is unknown may already have made the folder. It holds Create (button and Enter) until
  // the parent was read again; the hold survives leaving the form, because the write is still unsettled.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [held, setHeld] = useState<{ parent: string; name: string } | null>(null);
  // What the form shows now, read by the re-read after it finished (the render that started it is long gone).
  const form = useRef({ createOpen, folderName });
  form.current = { createOpen, folderName };
  const operationGeneration = useRef(0);
  const mounted = useRef(true);
  const currentParent = useRef<string | undefined>(undefined);
  const listing = useQuery({ queryKey: ['fs-dirs', path ?? ''], queryFn: () => elowenClient.browseDirs(path) });
  const createDirectory = useCreateDirectory();
  const data = listing.data;
  useEffect(() => { currentParent.current = data?.path; }, [data?.path]);
  useEffect(() => {
    // StrictMode replays setup after cleanup while keeping this ref alive.
    mounted.current = true;
    return () => {
      mounted.current = false;
      operationGeneration.current += 1;
    };
  }, []);

  const navigate = (nextPath: string | undefined) => {
    operationGeneration.current += 1;
    setCreateOpen(false);
    setFolderName('');
    setDuplicate(false);
    createDirectory.reset();
    setPath(nextPath);
  };

  const closePicker = () => {
    operationGeneration.current += 1;
    onClose();
  };

  const cancelCreate = () => {
    operationGeneration.current += 1;
    setCreateOpen(false);
    setFolderName('');
    setDuplicate(false);
    createDirectory.reset();
  };

  const submitDirectory = async () => {
    const name = folderName.trim();
    const parent = data?.path;
    if (!parent || !name || createDirectory.isPending || held) return;
    const generation = operationGeneration.current + 1;
    operationGeneration.current = generation;
    try {
      const created = await createDirectory.mutateAsync({ parent, name, listingPath: path });
      if (!mounted.current || operationGeneration.current !== generation || currentParent.current !== parent) return;
      setCreateOpen(false);
      setFolderName('');
      setPath(created.path);
      setReport({ key: CREATE_CARD_KEY, tone: 'ok', message: interpolate(t.projects.folderCreated, { name }) });
      createDirectory.reset();
    } catch (error) {
      if (!mounted.current) return;
      // A write whose outcome is unknown settles nothing even for a form the user has left.
      const unsettled = holdsAnswer(error);
      // A form the user has already left (cancelled, navigated away, closed) has nothing else to answer.
      if (!unsettled && operationGeneration.current !== generation) return;
      if (unsettled) setHeld({ parent, name });
      else if (error instanceof ElowenApiError && error.status === 409) { setDuplicate(true); return; }
      setFailure({ error });
    }
  };

  // The authoritative read of the target: the parent again, looking for the folder the lost write may have made.
  // Found: it is adopted (the form moves into it) instead of being created again. Not found: the create is
  // proven not to have happened and Create is free again. A read that fails verifies nothing and keeps the hold.
  const verifyCreated = releasing(async () => {
    if (!held) return;
    const { parent, name } = held;
    const fresh = await queryClient.fetchQuery({ queryKey: ['fs-dirs', parent], queryFn: () => elowenClient.browseDirs(parent), staleTime: 0 });
    void queryClient.invalidateQueries({ queryKey: ['fs-dirs'] });
    const found = fresh.entries.find((entry) => entry.name === name);
    if (found && mounted.current && form.current.createOpen && currentParent.current === parent && form.current.folderName.trim() === name) {
      setCreateOpen(false);
      setFolderName('');
      setPath(found.path);
    }
  }, () => { setHeld(null); setFailure(null); });

  return (
    <Modal title={t.projects.pickFolder} description={data?.path} onClose={closePicker} size="xl" icon={Folder}>
      <ModalReport report={report} />
      <OperationFailureReport reportKey={CREATE_CARD_KEY} failure={failure} message={t.projects.folderCreateError} reload={verifyCreated} />
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-2.5">
        <span className="min-w-0 flex-1 basis-full truncate font-mono text-small text-muted-foreground sm:basis-auto" title={data?.path}>{data?.path ?? '…'}</span>
        {allowCreateDirectory ? (
          <Button
            icon={FolderPlus}
            variant="ghost"
            disabled={!data?.path || createDirectory.isPending}
            onClick={() => { setCreateOpen(true); setDuplicate(false); createDirectory.reset(); }}
          >
            {t.projects.newFolder}
          </Button>
        ) : null}
        {data?.parent ? (
          <Button icon={FolderUp} variant="ghost" disabled={createDirectory.isPending} onClick={() => navigate(data.parent ?? undefined)}>{t.projects.parentFolder}</Button>
        ) : null}
      </div>
      {allowCreateDirectory && createOpen ? (
        <form
          className="border-b border-border bg-muted/35 px-5 py-3"
          onSubmit={(event) => { event.preventDefault(); void submitDirectory(); }}
        >
          <label htmlFor={folderNameId} className="sr-only">{t.projects.folderName}</label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id={folderNameId}
              value={folderName}
              onChange={(event) => { setFolderName(event.target.value); setDuplicate(false); }}
              placeholder={t.projects.folderNamePlaceholder}
              maxLength={255}
              aria-invalid={duplicate || undefined}
              aria-describedby={duplicate ? `${folderNameId}-error` : undefined}
              autoFocus
              className="min-w-0 flex-1"
            />
            <div className="flex items-center justify-end gap-2">
              <Button type="submit" variant="accent" disabled={!folderName.trim() || createDirectory.isPending || held !== null}>{t.projects.createFolder}</Button>
              <Button type="button" variant="ghost" onClick={cancelCreate}>{t.common.cancel}</Button>
            </div>
          </div>
          {duplicate ? <p id={`${folderNameId}-error`} role="alert" className="mt-2 text-meta text-destructive">{t.projects.folderExists}</p> : null}
        </form>
      ) : null}
      <ModalBody>
        {listing.isError ? (
          <ErrorState message={t.projects.folderError} onRetry={() => { void listing.refetch(); }} />
        ) : !data ? (
          <LoadingState />
        ) : data.entries.length === 0 ? (
          <EmptyState title={t.projects.folderEmpty} />
        ) : (
          <ul className="flex flex-col">
            {data.entries.map((entry) => (
              <li key={entry.path}>
                <button
                  type="button"
                  disabled={createDirectory.isPending}
                  onClick={() => navigate(entry.path)}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-card-title transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Folder size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                  <span className="truncate">{entry.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={closePicker}>{t.common.cancel}</Button>
        <Button variant="accent" icon={Check} disabled={!data?.path || createDirectory.isPending} onClick={() => { if (data?.path) onSelect(data.path); }}>{t.projects.selectFolder}</Button>
      </ModalFooter>
    </Modal>
  );
}
